import React, { useState, useEffect, useRef } from "react";
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Avatar,
  TextField,
  Paper,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import CancelIcon from "@mui/icons-material/Cancel";
import SaveIcon from "@mui/icons-material/Save";
import moment from "moment";
import {
  apiCallSendClaimChat,
  apiCallGetClaimedChat,
  apiCallClaimMessageEdit,
  apiCallClaimMessageDelete,
} from "../../../../../Core/Apicall";
import { useSelector } from "react-redux";
import { RootState } from "../../../../../Redux/Store";

interface ChatDrawerProps {
  open: boolean;
  onClose: () => void;
  user: {
    fullName: string;
    profileImagePath?: string;
    reason?: any;
    reportId?: any;
  } | null;
}

interface Message {
  _id: any;
  senderId: number;
  message: string;
  createdAt: any;
}

const ChatDrawer: React.FC<ChatDrawerProps> = ({ open, onClose, user }) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const userdata: any = useSelector((state: RootState) => state.global.token);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteMsgId, setDeleteMsgId] = useState<number | null>(null);

  const [input, setInput] = useState("");
  const [editingId, setEditingId] = useState<number | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuMsgId, setMenuMsgId] = useState<number | null>(null);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const prevMsgCount = useRef(messages.length);

  useEffect(() => {
    if (user?.reportId) {
      fetchChats();
    }
  }, [user?.reportId]);

  const fetchChats = async () => {
    try {
      const el = scrollRef.current;
      const res = await apiCallGetClaimedChat({
        reportId: user?.reportId,
      });

      if (res.success) {
        setMessages(res.data || []);
        setTimeout(() => {
          if (el) {
            el.scrollTop = el.scrollHeight;
          }
        }, 50);
      }
    } catch (error) {
    } finally {
    }
  };

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    if (messages.length > prevMsgCount.current) {
      el.scrollTop = el.scrollHeight;
    }
    prevMsgCount.current = messages.length;
  }, [messages]);

  const handleSend = async () => {
    if (!input.trim()) return;

    if (editingId) {
      setMessages((prev) =>
        prev.map((m) =>
          m._id === editingId ? { ...m, message: input.trim() } : m
        )
      );

       await apiCallClaimMessageEdit({
        messageId: editingId,
        senderId: userdata?.data?.userId,
        message: input.trim(),
      });

      setEditingId(null);
      setInput("");
    } else {
      const res = await apiCallSendClaimChat({
        reportId: user?.reportId,
        senderId: userdata?.data?.userId,
        message: input.trim(),
      });

      const newMsg: Message = {
        _id: res.data._id,
        senderId: userdata?.data?.userId,
        message: input.trim(),
        createdAt: new Date(),
      };
      setMessages((prev) => [...prev, newMsg]);
      setInput("");
    }
  };

  // ✅ Edit message
  const handleEdit = (_id: number, message: string) => {
    setEditingId(_id);
    setInput(message);
    handleMenuClose();
  };

  const confirmDelete = (_id: number) => {
    setDeleteMsgId(_id);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (deleteMsgId !== null) {
      await apiCallClaimMessageDelete({
        messageId: deleteMsgId,
        userId: userdata?.data?.userId,
        isDelete: true,
      });
      setMessages((prev) => prev.filter((msg) => msg._id !== deleteMsgId));
      handleMenuClose();
      setDeleteDialogOpen(false);
      setDeleteMsgId(null);
    }
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setDeleteMsgId(null);
  };

  // ✅ Cancel edit
  const handleCancelEdit = () => {
    setEditingId(null);
    setInput("");
  };

  // ✅ Menu controls
  const handleMenuOpen = (
    e: React.MouseEvent<HTMLButtonElement>,
    _id: number
  ) => {
    setAnchorEl(e.currentTarget);
    setMenuMsgId(_id);
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuMsgId(null);
  };
  let lastDate: string | null = null;

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box
        sx={{
          width: { xs: "100%", sm: "80%", md: "700px" },
          height: "100%",
          display: "flex",
          flexDirection: "column",
          bgcolor: "#fff",
          color: "#222",
        }}
      >
        {/* ✅ Header */}
        <Paper
          elevation={1}
          sx={{
            display: "flex",
            alignItems: "center",
            p: 2,
            borderBottom: "1px solid #eee",
          }}
        >
          <Avatar src={user?.profileImagePath} sx={{ mr: 2 }} />
          <Box flex={1}>
            <Typography variant="h6">{user?.fullName}</Typography>
            <Typography variant="body2" sx={{ color: "#777" }}>
              {user?.reason}
            </Typography>
          </Box>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Paper>

        <Box
          ref={scrollRef}
          sx={{
            flex: 1,
            overflowY: "auto",
            p: 3,
            display: "flex",
            flexDirection: "column",
            gap: 2,
            bgcolor: "#f7f8fa",

            /* ✅ Custom scrollbar styling */
            "&::-webkit-scrollbar": {
              width: "8px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "#ccc",
              borderRadius: "8px",
            },
            "&::-webkit-scrollbar-thumb:hover": {
              backgroundColor: "#999",
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: "transparent",
            },
          }}
        >
          {messages.length === 0 ? (
            <Box
              sx={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#999",
                fontSize: 14,
              }}
            >
              No messages yet
            </Box>
          ) : (
            messages.map((msg) => {
              const isAdmin = msg?.senderId === userdata?.data?.userId;
              const msgDate = moment(msg.createdAt).startOf("day");
              const today = moment().startOf("day");
              const yesterday = moment().subtract(1, "day").startOf("day");

              let dateLabel = "";
              if (!lastDate || !msgDate.isSame(moment(lastDate))) {
                if (msgDate.isSame(today)) dateLabel = "Today";
                else if (msgDate.isSame(yesterday)) dateLabel = "Yesterday";
                else dateLabel = msgDate.format("DD MMM YYYY");
                lastDate = msgDate.toString();
              }

              return (
                <Box
                  key={msg._id}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: isAdmin ? "flex-end" : "flex-start",
                  }}
                >
                  {dateLabel && (
                    <Box
                      sx={{
                        width: "100%",
                        display: "flex",
                        justifyContent: "center",
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          color: "#555",
                          fontSize: 12,
                          mt: 2,
                          mb: 2,
                          px: 1,
                          bgcolor: "#e0e0e0",
                          borderRadius: 2,
                        }}
                      >
                        {dateLabel}
                      </Typography>
                    </Box>
                  )}
                  <Paper
                    elevation={1}
                    sx={{
                      bgcolor: "transparent",
                      color: "#111",
                      border: isAdmin ? "1px solid #000" : "1px solid #ddd",
                      borderRadius: "14px",
                      px: 2,
                      py: 1.2,
                      maxWidth: "75%",
                      display: "inline-block",
                      position: "relative",
                      wordBreak: "break-word",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: 14,
                        lineHeight: 1.5,
                        pr: isAdmin ? 3 : 0,
                      }}
                    >
                      {msg.message}
                    </Typography>

                    {/* 3-dot menu only for admin */}
                    {isAdmin && (
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuOpen(e, msg._id)}
                        sx={{
                          position: "absolute",
                          top: 2,
                          right: 2,
                          color: "#999",
                          "&:hover": { color: "#000" },
                        }}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Paper>

                  <Typography
                    variant="caption"
                    sx={{
                      color: "#999",
                      fontSize: 11,
                      mt: 0.5,
                      textAlign: isAdmin ? "right" : "left",
                    }}
                  >
                    {moment(msg.createdAt).format("hh:mm a")}
                  </Typography>
                </Box>
              );
            })
          )}
          <div ref={messagesEndRef} />
        </Box>

        {/* ✅ Menu for edit/delete */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <MenuItem
            onClick={() => {
              if (menuMsgId !== null) {
                const msg = messages.find((m) => m._id === menuMsgId);
                if (msg) handleEdit(msg._id, msg.message);
              }
            }}
          >
            Edit
          </MenuItem>
          <MenuItem
            onClick={() => {
              if (menuMsgId !== null) confirmDelete(menuMsgId);
            }}
          >
            Delete
          </MenuItem>
        </Menu>

        {/* ✅ Bottom input section */}
        <Paper
          elevation={3}
          sx={{
            display: "flex",
            alignItems: "center",
            borderTop: "1px solid #eee",
            p: 1.5,
            bgcolor: "#fff",
            gap: 1,
          }}
        >
          <TextField
            size="small"
            fullWidth
            placeholder={
              editingId ? "Edit your message..." : "Type your message..."
            }
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSend()}
            InputProps={{
              sx: {
                borderRadius: "20px",
                px: 2,
                bgcolor: "#f9f9f9",
                fontSize: 15,
                color: "#000", // text color always black
                caretColor: "#000",
                "&.Mui-focused": {
                  borderColor: "#000", // focus border color
                },
              },
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  borderColor: "#ccc", // default border
                },
                "&:hover fieldset": {
                  borderColor: "#000", // hover border
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#000", // focus border
                  borderWidth: 2,
                },
              },
              "& .MuiInputBase-input::selection": {
                backgroundColor: "#000",
                color: "#fff",
              },
            }}
          />

          {editingId ? (
            <>
              <IconButton
                onClick={handleSend}
                sx={{
                  bgcolor: "#000",
                  color: "#fff",
                  "&:hover": { bgcolor: "#000" },
                }}
              >
                <SaveIcon />
              </IconButton>
              <IconButton
                onClick={handleCancelEdit}
                sx={{
                  bgcolor: "#000",
                  color: "#fff",
                  "&:hover": { bgcolor: "#000" },
                }}
              >
                <CancelIcon />
              </IconButton>
            </>
          ) : (
            <IconButton
              onClick={handleSend}
              sx={{
                bgcolor: "#000",
                color: "#fff",
                "&:hover": { bgcolor: "#000" },
              }}
            >
              <SendIcon />
            </IconButton>
          )}
        </Paper>
      </Box>

      <Dialog open={deleteDialogOpen} onClose={handleCancelDelete}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          Are you sure you want to delete this message?
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} color="primary">
            Cancel
          </Button>
          <Button onClick={handleConfirmDelete} color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Drawer>
  );
};

export default ChatDrawer;
